import { useLocation, useNavigate } from 'react-router-dom';
import { useMemo } from 'react';
import type { PageHeaderBreadcrumb } from '@/components/ui/PageHeader';

/**
 * Breadcrumb configuration for different routes
 */
interface BreadcrumbConfig {
  path: string;
  label: string;
  parent?: string;
  dynamic?: boolean;
  icon?: React.ReactNode;
  hideInNav?: boolean;
}

/**
 * Global breadcrumb configuration for the entire application
 */
const GLOBAL_BREADCRUMB_CONFIG: Record<string, BreadcrumbConfig> = {
  // Root paths
  '/': {
    path: '/',
    label: 'Dashboard',
  },
  '/dashboard': {
    path: '/dashboard',
    label: 'Dashboard',
  },

  // Transaction module paths
  '/transactions': {
    path: '/transactions',
    label: 'Transactions',
    parent: '/dashboard',
  },
  '/transactions/:transactionId': {
    path: '/transactions/:transactionId',
    label: 'Transaction Details',
    parent: '/transactions',
    dynamic: true,
  },
  '/transactions/create': {
    path: '/transactions/create',
    label: 'Create Transaction',
    parent: '/transactions',
  },
  '/transactions/export': {
    path: '/transactions/export',
    label: 'Export Transactions',
    parent: '/transactions',
  },
  '/transactions/reports': {
    path: '/transactions/reports',
    label: 'Transaction Reports',
    parent: '/transactions',
  },
  '/transactions/settings': {
    path: '/transactions/settings',
    label: 'Transaction Settings',
    parent: '/transactions',
  },

  // User management paths
  '/users': {
    path: '/users',
    label: 'Users',
    parent: '/dashboard',
  },
  '/users/create': {
    path: '/users/create',
    label: 'Create User',
    parent: '/users',
  },

  // Settings paths
  '/settings': {
    path: '/settings',
    label: 'Settings',
    parent: '/dashboard',
  },
  '/settings/profile': {
    path: '/settings/profile',
    label: 'Profile Settings',
    parent: '/settings',
  },
};

/**
 * Utility function to match dynamic routes
 */
const matchRoute = (
  pattern: string,
  pathname: string
): { isMatch: boolean; params: Record<string, string> } => {
  const patternParts = pattern.split('/');
  const pathParts = pathname.split('/');

  if (patternParts.length !== pathParts.length) {
    return { isMatch: false, params: {} };
  }

  const params: Record<string, string> = {};

  for (let i = 0; i < patternParts.length; i++) {
    const patternPart = patternParts[i];
    const pathPart = pathParts[i];

    if (patternPart.startsWith(':')) {
      // Dynamic segment
      params[patternPart.slice(1)] = pathPart;
    } else if (patternPart !== pathPart) {
      // Static segment doesn't match
      return { isMatch: false, params: {} };
    }
  }

  return { isMatch: true, params };
};

/**
 * Common breadcrumb hook for the entire application
 */
export const useCommonBreadcrumbs = (options?: {
  hideBreadcrumbs?: boolean;
  customBreadcrumbs?: PageHeaderBreadcrumb[];
  context?: Record<string, unknown>;
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const breadcrumbs: PageHeaderBreadcrumb[] = useMemo(() => {
    // If breadcrumbs should be hidden, return empty array
    if (options?.hideBreadcrumbs) {
      return [];
    }

    // If custom breadcrumbs are provided, use them
    if (options?.customBreadcrumbs) {
      return options.customBreadcrumbs.map(breadcrumb => ({
        ...breadcrumb,
        onClick:
          breadcrumb.onClick || (breadcrumb.href ? () => navigate(breadcrumb.href!) : undefined),
      }));
    }

    const currentPath = location.pathname;

    // Find matching route configuration
    let matchedConfig: BreadcrumbConfig | undefined;
    let routeParams: Record<string, string> = {};

    // First try exact match
    if (GLOBAL_BREADCRUMB_CONFIG[currentPath]) {
      matchedConfig = GLOBAL_BREADCRUMB_CONFIG[currentPath];
    } else {
      // Try dynamic route matching
      for (const [pattern, config] of Object.entries(GLOBAL_BREADCRUMB_CONFIG)) {
        if (pattern.includes(':')) {
          const { isMatch, params } = matchRoute(pattern, currentPath);
          if (isMatch) {
            matchedConfig = config;
            routeParams = params;
            break;
          }
        }
      }
    }

    // If no configuration found, don't show breadcrumbs
    if (!matchedConfig) {
      return [];
    }

    // Build breadcrumb chain
    const breadcrumbChain: PageHeaderBreadcrumb[] = [];
    let config: BreadcrumbConfig | undefined = matchedConfig;

    // Build the chain by traversing parents
    while (config) {
      const isActive =
        config.path === currentPath ||
        Object.keys(GLOBAL_BREADCRUMB_CONFIG).some(pattern => {
          if (pattern.includes(':')) {
            return (
              matchRoute(pattern, currentPath).isMatch &&
              GLOBAL_BREADCRUMB_CONFIG[pattern] === config
            );
          }
          return false;
        });

      let label = config.label;

      // Handle dynamic labels based on context and route params
      if (config.dynamic && (options?.context || Object.keys(routeParams).length > 0)) {
        if (config.path.includes('/transactions/:transactionId') && routeParams.transactionId) {
          label = `Transaction ${routeParams.transactionId}`;
        } else if (options?.context?.transactionId) {
          label = `Transaction ${options.context.transactionId}`;
        } else if (options?.context?.customLabel) {
          label = options.context.customLabel as string;
        }
      }

      // Don't add breadcrumb if it should be hidden in navigation
      if (!config.hideInNav) {
        breadcrumbChain.unshift({
          label,
          href: config.path.includes(':') ? currentPath : config.path,
          onClick: () => {
            if (config!.path.includes(':')) {
              // For dynamic routes, stay on current page if it's active
              if (isActive) return;
              navigate(currentPath);
            } else {
              navigate(config!.path);
            }
          },
          active: isActive,
        });
      }

      // Move to parent
      config = config.parent ? GLOBAL_BREADCRUMB_CONFIG[config.parent] : undefined;
    }

    return breadcrumbChain;
  }, [location.pathname, options, navigate]);

  return breadcrumbs;
};

/**
 * Transaction-specific breadcrumb hook (backward compatibility)
 */
export const useTransactionBreadcrumbs = (
  transactionId?: string,
  merchantTransactionId?: string,
  paymentGateway?: string,
  hideBreadcrumbs?: boolean
) => {
  return useCommonBreadcrumbs({
    hideBreadcrumbs,
    context: {
      transactionId,
      merchantTransactionId,
      paymentGateway,
    },
  });
};

/**
 * Utility function to create custom breadcrumbs
 */
export const createBreadcrumbs = (
  items: Array<{
    label: string;
    href?: string;
    onClick?: () => void;
    active?: boolean;
  }>
): PageHeaderBreadcrumb[] => {
  return items.map((item, index) => ({
    ...item,
    active: item.active ?? index === items.length - 1,
  }));
};

/**
 * Pre-defined breadcrumb sets for common scenarios
 */
export const COMMON_BREADCRUMBS = {
  // Dashboard only
  dashboard: (): PageHeaderBreadcrumb[] =>
    createBreadcrumbs([{ label: 'Dashboard', active: true }]),

  // Transaction breadcrumbs
  transactionList: (): PageHeaderBreadcrumb[] =>
    createBreadcrumbs([
      { label: 'Dashboard', href: '/dashboard' },
      { label: 'Transactions', active: true },
    ]),

  transactionView: (transactionId: string): PageHeaderBreadcrumb[] =>
    createBreadcrumbs([
      { label: 'Dashboard', href: '/dashboard' },
      { label: 'Transactions', href: '/transactions' },
      { label: `Transaction ${transactionId}`, active: true },
    ]),

  transactionCreate: (): PageHeaderBreadcrumb[] =>
    createBreadcrumbs([
      { label: 'Dashboard', href: '/dashboard' },
      { label: 'Transactions', href: '/transactions' },
      { label: 'Create Transaction', active: true },
    ]),

  // User breadcrumbs
  userList: (): PageHeaderBreadcrumb[] =>
    createBreadcrumbs([
      { label: 'Dashboard', href: '/dashboard' },
      { label: 'Users', active: true },
    ]),

  userCreate: (): PageHeaderBreadcrumb[] =>
    createBreadcrumbs([
      { label: 'Dashboard', href: '/dashboard' },
      { label: 'Users', href: '/users' },
      { label: 'Create User', active: true },
    ]),

  // Settings breadcrumbs
  settings: (): PageHeaderBreadcrumb[] =>
    createBreadcrumbs([
      { label: 'Dashboard', href: '/dashboard' },
      { label: 'Settings', active: true },
    ]),
};

/**
 * Hook to conditionally show/hide breadcrumbs
 */
export const useBreadcrumbVisibility = (shouldShow: boolean = true) => {
  const breadcrumbs = useCommonBreadcrumbs({
    hideBreadcrumbs: !shouldShow,
  });

  return shouldShow ? breadcrumbs : [];
};

// Export the configuration for external use
export { GLOBAL_BREADCRUMB_CONFIG };

// Backward compatibility
export const useBreadcrumbs = useCommonBreadcrumbs;
export const TRANSACTION_BREADCRUMBS = COMMON_BREADCRUMBS;

export default useCommonBreadcrumbs;
